import React, { useState } from 'react'

import { FileInput, Input, InputGroup } from '@components'
import Button from '@components/Button'
import Spinner from '@components/Spinner'
import Status from '@components/Status'
import { successMessages } from '@constants/feedbackMessages'
import {
	describeAmendmentAction,
	LegalAmendment,
	LegalAmendmentAction,
	LegalAmendmentPlanEntry,
	LegalAmendmentRegistration,
} from '@models/legalDocument.model'
import { legalDocumentApi } from '@services/LegalDocumentApi'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'

const styles = {
	card: 'border border-gray-light rounded-md px-4 py-3',
	panel: 'mt-4 p-6 bg-white rounded-sm shadow-5',
	label: 'block text-sm text-gray',
	crumb: 'text-xs text-gray',
	address: 'font-mono text-sm text-primary',
	textarea:
		'w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary',
	warning:
		'border-l-4 border-yellow bg-yellow-transparent px-4 py-3 rounded-r-md',
	applied:
		'mt-4 border-l-4 border-green bg-green-transparent px-4 py-3 rounded-r-md',
	failed:
		'mt-4 border-l-4 border-red bg-red-transparent px-4 py-3 rounded-r-md',
}

const ACTION_ORDER: LegalAmendmentAction[] = [
	'redacao-nova',
	'incluido',
	'revogado',
	'inalterado',
]

type Failure = { data?: { message?: string; detail?: string } }

// Citação de dispositivo tem vírgula ("art. 5º, § 2º"): a lista vai uma por linha.
const toLines = (value: string): string[] =>
	value
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line.length > 0)

const formatDay = (value: string) =>
	new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR')

const formatMoment = (value: string) =>
	new Date(value).toLocaleString('pt-BR', {
		dateStyle: 'short',
		timeStyle: 'short',
	})

const nameOf = (amendment: Pick<LegalAmendment, 'number' | 'year'>) =>
	`EC ${amendment.number}/${amendment.year}`

// A resposta traz uma linha por emenda da norma; a desta é a que começa pelo nome dela.
const summarize = (registration: LegalAmendmentRegistration) => {
	const name = nameOf(registration.amendment)
	const own = registration.warnings.find((warning) =>
		warning.startsWith(`A ${name} `)
	)

	return {
		name,
		own,
		failed: Boolean(own?.includes('não foi aplicada')),
		otherFailures: registration.warnings.filter(
			(warning) => warning !== own && warning.includes('não foi aplicada')
		),
	}
}

function FailureMessage({ error }: { error: unknown }) {
	const failure = (error as Failure | undefined)?.data

	if (!failure) {
		return null
	}

	return (
		<div className='mt-4'>
			<p className='text-red'>{failure.message}</p>
			{failure.detail && (
				<p className='mt-1 text-sm text-red'>{failure.detail}</p>
			)}
		</div>
	)
}

function PlanEntries({ plan }: { plan: LegalAmendmentPlanEntry[] }) {
	return (
		<div className='flex flex-col gap-2 mt-2'>
			{plan.map((entry) => {
				const action = describeAmendmentAction(entry.action)

				return (
					<div key={`${entry.action}-${entry.path}`} className={styles.card}>
						<div className='flex gap-3 items-center flex-wrap'>
							<span className={styles.address}>{entry.path}</span>
							<Status status={action.tone} className='px-3 py-1 text-sm'>
								<>{action.label}</>
							</Status>
						</div>

						{entry.text && (
							<p className='mt-2 whitespace-pre-wrap leading-relaxed'>
								{entry.text}
							</p>
						)}
					</div>
				)
			})}
		</div>
	)
}

function AppliedResult({
	registration,
	onDismiss,
}: {
	registration: LegalAmendmentRegistration
	onDismiss: () => void
}) {
	const [showEntries, setShowEntries] = useState(false)

	const { name, own, failed, otherFailures } = summarize(registration)

	const counts = ACTION_ORDER.map((action) => ({
		label: describeAmendmentAction(action).label.toLowerCase(),
		total: registration.plan.filter((entry) => entry.action === action).length,
	})).filter((count) => count.total > 0)

	return (
		<div className={failed ? styles.failed : styles.applied}>
			<div className='flex gap-3 items-start'>
				<div className='flex-1'>
					<p className='font-medium'>
						{failed ? `${name} não foi aplicada` : `${name} aplicada`}
					</p>

					{failed ? (
						<p className='mt-1 text-sm leading-relaxed'>{own}</p>
					) : (
						<p className='mt-1 text-sm'>
							{counts
								.map((count) => `${count.total} ${count.label}`)
								.join(' · ')}
						</p>
					)}
				</div>

				<Button variant='outline' onClick={onDismiss}>
					<>Fechar</>
				</Button>
			</div>

			{!failed && registration.plan.length > 0 && (
				<>
					<button
						type='button'
						className='mt-2 text-sm text-primary'
						onClick={() => setShowEntries(!showEntries)}
					>
						{showEntries ? 'Esconder dispositivos' : 'Ver dispositivos'}
					</button>

					{showEntries && <PlanEntries plan={registration.plan} />}
				</>
			)}

			{otherFailures.length > 0 && (
				<div className='flex flex-col gap-3 mt-3'>
					{otherFailures.map((warning) => (
						<p key={warning} className={styles.warning}>
							{warning}
						</p>
					))}
				</div>
			)}

			{!failed && registration.pending && (
				<p className='mt-3 text-sm text-gray'>{registration.pending}</p>
			)}
		</div>
	)
}

function AmendmentForm({
	id,
	amendment,
	onApplied,
	onClose,
}: {
	id: string
	amendment?: LegalAmendment
	onApplied: (registration: LegalAmendmentRegistration) => void
	onClose: () => void
}) {
	const [file, setFile] = useState<File | null>(null)
	const [fileInputKey, setFileInputKey] = useState(0)
	const [number, setNumber] = useState('')
	const [year, setYear] = useState('')
	const [promulgatedOn, setPromulgatedOn] = useState(
		amendment?.promulgatedOn ?? ''
	)
	const [sourceUrl, setSourceUrl] = useState(amendment?.sourceUrl ?? '')
	const [revoked, setRevoked] = useState(amendment?.revoked.join('\n') ?? '')
	const [simulation, setSimulation] =
		useState<LegalAmendmentRegistration | null>(null)

	const [register, registering] =
		legalDocumentApi.useRegisterAmendmentMutation()
	const [update, updating] = legalDocumentApi.useUpdateAmendmentMutation()

	const isEditing = Boolean(amendment?.id)
	const isSending = registering.isLoading || updating.isLoading
	const canSend = isEditing || file !== null

	const clear = () => {
		setFile(null)
		setFileInputKey((current) => current + 1)
		setNumber('')
		setYear('')
		setPromulgatedOn('')
		setSourceUrl('')
		setRevoked('')
		setSimulation(null)
		registering.reset()
	}

	const send = async (simulate: boolean) => {
		const fields = {
			id,
			revoked: toLines(revoked),
			simulate,
			...(promulgatedOn ? { promulgatedOn } : {}),
			...(sourceUrl.trim() ? { sourceUrl: sourceUrl.trim() } : {}),
		}

		try {
			const registration =
				isEditing && amendment?.id
					? await update({
							...fields,
							amendmentId: amendment.id,
							...(file ? { file } : {}),
					  }).unwrap()
					: await register({
							...fields,
							file: file as File,
							...(number ? { number } : {}),
							...(year ? { year } : {}),
					  }).unwrap()

			if (registration.simulated) {
				setSimulation(registration)

				return
			}

			onApplied(registration)

			if (isEditing) {
				onClose()
			} else {
				clear()
			}
		} catch {
			/* A recusa do servidor aparece no painel, com o motivo. */
		}
	}

	return (
		<div className={styles.panel}>
			<p className='text-gray max-w-[820px] mb-5 leading-relaxed'>
				{isEditing
					? 'Corrigir relê a norma do documento original e reaplica as emendas. Envie outro PDF só se o ato guardado estiver errado.'
					: 'Número, ano e data saem do próprio ato quando ficam em branco. Simule e confira o plano contra o PDF antes de aplicar.'}
			</p>

			<FileInput
				key={fileInputKey}
				accept='application/pdf,text/plain,.txt'
				onChange={(files: File[]) => {
					setFile(files[0] ?? null)
					setSimulation(null)
				}}
			>
				<>{isEditing ? 'Trocar o PDF do ato (opcional)' : 'PDF do ato'}</>
			</FileInput>

			<div className='grid grid-cols-2 gap-4 mt-4'>
				{!isEditing && (
					<>
						<InputGroup label='Número da emenda' id='amendmentNumber'>
							<Input
								type='number'
								value={number}
								placeholder='lido do ato'
								onChange={(event) => setNumber(event.target.value)}
							/>
						</InputGroup>

						<InputGroup label='Ano' id='amendmentYear'>
							<Input
								type='number'
								value={year}
								placeholder='lido do ato'
								onChange={(event) => setYear(event.target.value)}
							/>
						</InputGroup>
					</>
				)}

				<InputGroup label='Promulgada em' id='amendmentPromulgatedOn'>
					<Input
						type='date'
						value={promulgatedOn}
						onChange={(event) => setPromulgatedOn(event.target.value)}
					/>
				</InputGroup>

				<InputGroup label='Endereço de onde o ato veio' id='amendmentSourceUrl'>
					<Input
						type='url'
						value={sourceUrl}
						placeholder='https://...'
						onChange={(event) => setSourceUrl(event.target.value)}
					/>
				</InputGroup>
			</div>

			<InputGroup
				label='O que o ato revoga fora das aspas'
				id='amendmentRevoked'
				hint='Só a citação do dispositivo, um por linha: "parágrafo único do art. 111". Em branco quando o ato não revoga nada.'
			>
				<textarea
					id='amendmentRevoked'
					rows={3}
					className={styles.textarea}
					value={revoked}
					onChange={(event) => setRevoked(event.target.value)}
				/>
			</InputGroup>

			<FailureMessage error={registering.error ?? updating.error} />

			{simulation && (
				<div className='mt-6'>
					<span className={styles.label}>
						O que mudaria na norma. Confira contra o PDF antes de aplicar.
					</span>
					<PlanEntries plan={simulation.plan} />
				</div>
			)}

			<div className='flex gap-2 mt-6'>
				<Button
					variant='outline'
					disabled={!canSend || isSending}
					onClick={() => send(true)}
				>
					<>Simular</>
				</Button>

				<Button disabled={!canSend || isSending} onClick={() => send(false)}>
					{isSending ? 'Enviando...' : 'Aplicar'}
				</Button>

				<Button variant='outline' onClick={onClose}>
					<>{isEditing ? 'Cancelar' : 'Fechar'}</>
				</Button>
			</div>
		</div>
	)
}

export default function Amendments({ id }: { id: string }) {
	const dispatch = useAppDispatch()

	const [adding, setAdding] = useState(false)
	const [editing, setEditing] = useState<string | null>(null)
	const [confirming, setConfirming] = useState<string | null>(null)
	const [applied, setApplied] = useState<LegalAmendmentRegistration | null>(
		null
	)

	const { data, isFetching } = legalDocumentApi.useAmendmentsQuery({ id })

	const [remove, removing] = legalDocumentApi.useRemoveAmendmentMutation()

	const onApplied = (registration: LegalAmendmentRegistration) => {
		setApplied(registration)

		if (!summarize(registration).failed) {
			dispatch(notificationCreators.show(successMessages.appliedLegalAmendment))
		}
	}

	const onRemove = async (amendmentId: string) => {
		try {
			await remove({ id, amendmentId }).unwrap()

			setConfirming(null)
			setApplied(null)
			dispatch(notificationCreators.show(successMessages.removedLegalAmendment))
		} catch {
			/* A recusa do servidor aparece abaixo da emenda, com o motivo. */
		}
	}

	return (
		<div className='mt-6'>
			<div className='flex gap-4 items-start flex-wrap'>
				<p className='text-gray max-w-[720px] leading-relaxed'>
					Emendas estaduais que a edição consolidada da norma ainda não traz.
					Cada uma é aplicada por cima do documento original, na ordem da
					promulgação, e volta sozinha quando a norma é recarregada.
				</p>

				<div className='flex-1' />

				{!adding && (
					<Button onClick={() => setAdding(true)}>
						<>Cadastrar emenda</>
					</Button>
				)}
			</div>

			{applied && (
				<AppliedResult
					registration={applied}
					onDismiss={() => setApplied(null)}
				/>
			)}

			{adding && (
				<AmendmentForm
					id={id}
					onApplied={onApplied}
					onClose={() => setAdding(false)}
				/>
			)}

			{isFetching && !data && (
				<div className='flex justify-center mt-8'>
					<Spinner size='md' />
				</div>
			)}

			{data && data.data.length === 0 && !adding && (
				<p className='text-gray mt-6'>Nenhuma emenda cadastrada nesta norma.</p>
			)}

			<div className='flex flex-col gap-3 mt-6'>
				{data?.data.map((amendment) => {
					const key = amendment.id ?? `${amendment.number}-${amendment.year}`

					return (
						<div key={key} className={styles.card}>
							<div className='flex gap-3 items-center flex-wrap'>
								<span className='font-medium'>{nameOf(amendment)}</span>

								<span className={styles.crumb}>
									promulgada em {formatDay(amendment.promulgatedOn)}
								</span>

								<Status
									status={amendment.hasAct ? 'success' : 'danger'}
									className='px-3 py-1 text-sm'
								>
									<>
										{amendment.hasAct ? 'Ato guardado' : 'Falta o PDF do ato'}
									</>
								</Status>

								<div className='flex-1' />

								<Button
									variant='outline'
									onClick={() =>
										setEditing(editing === amendment.id ? null : amendment.id)
									}
								>
									<>Corrigir</>
								</Button>

								<Button
									variant='outline'
									onClick={() => setConfirming(amendment.id)}
								>
									<>Remover</>
								</Button>
							</div>

							{amendment.revoked.length > 0 && (
								<p className={`${styles.crumb} mt-2`}>
									Revoga: {amendment.revoked.join(', ')}
								</p>
							)}

							{amendment.lastReport && (
								<p className='mt-2 text-sm leading-relaxed'>
									{amendment.lastReport}
									{amendment.lastAppliedAt && (
										<span className={`${styles.crumb} ml-2`}>
											{formatMoment(amendment.lastAppliedAt)}
										</span>
									)}
								</p>
							)}

							{confirming === amendment.id && amendment.id && (
								<div className='mt-3'>
									<p className='text-red max-w-[820px]'>
										Remover relê a norma do documento original e reaplica as
										outras emendas. O texto que esta emenda trouxe deixa de
										valer.
									</p>

									<div className='flex gap-2 mt-3'>
										<Button
											disabled={removing.isLoading}
											onClick={() => onRemove(amendment.id as string)}
										>
											{removing.isLoading
												? 'Removendo...'
												: 'Confirmar remoção'}
										</Button>
										<Button
											variant='outline'
											onClick={() => setConfirming(null)}
										>
											<>Cancelar</>
										</Button>
									</div>

									<FailureMessage error={removing.error} />
								</div>
							)}

							{editing === amendment.id && (
								<AmendmentForm
									id={id}
									amendment={amendment}
									onApplied={onApplied}
									onClose={() => setEditing(null)}
								/>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
